import { useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button,
} from '@mui/material';
import { fireSessionConfetti } from '@/utils/confetti';

export type HoursMilestone = { type: 'hours'; hours: number };
export type WeekStreakMilestone = { type: 'week'; weekKey: string };
export type Milestone = HoursMilestone | WeekStreakMilestone;

interface ListeningMilestoneModalProps {
  milestone: Milestone;
  onDismiss: () => void;
}

const MOTIVATIONAL: Record<number, string> = {
  10: "You've listened to 10 hours of real content. That's huge.",
  20: "20 hours in — your ear is getting tuned.",
  50: "50 hours of real language. You're building fluency.",
  100: "100 hours! You're in rare company.",
};

function getHoursMessage(hours: number): string {
  return MOTIVATIONAL[hours] ?? `${hours} hours of real content. Keep it up!`;
}

export function ListeningMilestoneModal({ milestone, onDismiss }: ListeningMilestoneModalProps) {
  useEffect(() => {
    const cleanup = fireSessionConfetti();
    return cleanup;
  }, []);

  const isWeek = milestone.type === 'week';

  return (
    <Dialog open onClose={onDismiss} maxWidth="xs" fullWidth>
      <DialogContent
        sx={{
          textAlign: 'center',
          p: 4,
          background: isWeek
            ? 'linear-gradient(160deg, #1a2e1a, #0d1f0d)'
            : 'linear-gradient(160deg, #1a1a2e, #0d1b2a)',
          border: isWeek
            ? '1px solid rgba(62,207,130,0.3)'
            : '1px solid rgba(255,215,0,0.3)',
          borderRadius: 3,
        }}
      >
        <Typography fontSize={44} lineHeight={1} mb={0.75}>
          {isWeek ? '🔥' : '🎧'}
        </Typography>

        <Typography
          variant="overline"
          display="block"
          sx={{ color: isWeek ? '#3ECF82' : '#ffd700', letterSpacing: 3, mb: 0.75 }}
        >
          {isWeek ? 'Perfect Week' : 'Milestone Unlocked'}
        </Typography>

        <Typography variant="h5" fontWeight={700} color="#ffffff" mb={0.5}>
          {isWeek ? '7 Days Straight!' : `${milestone.hours} Hours!`}
        </Typography>

        <Typography variant="body2" color="rgba(255,255,255,0.7)" mb={isWeek ? 2 : 2.5}>
          {isWeek
            ? "You listened every single day this week. Consistency is how fluency happens."
            : getHoursMessage(milestone.hours)}
        </Typography>

        {isWeek && <WeekDots />}

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={onDismiss}
          sx={{
            mt: isWeek ? 2 : 0,
            borderRadius: 2,
            background: isWeek
              ? 'linear-gradient(135deg, #3ECF82, #00b894)'
              : 'linear-gradient(135deg, #ffd700, #ff8c00)',
            color: isWeek ? '#0d1f0d' : '#1a1a1a',
            fontWeight: 700,
            '&:hover': {
              background: isWeek
                ? 'linear-gradient(135deg, #2db870, #009b7d)'
                : 'linear-gradient(135deg, #e6c200, #e07800)',
            },
          }}
        >
          {isWeek ? 'Keep the streak 🔥' : 'Keep Listening 🎉'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function WeekDots() {
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  return (
    <Box display="flex" gap={0.75} justifyContent="center">
      {days.map((d, i) => (
        <Box
          key={`day-${i}`}
          sx={{
            width: 32, height: 32, borderRadius: '50%',
            background: '#3ECF82',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: '#0d1f0d',
          }}
        >
          {d}
        </Box>
      ))}
    </Box>
  );
}
